// ScanPage — public /scan?cp=&uid=&sdmcounter=&sdmmac=[&nonce=] landing.
//
// Spec: docs/specs/location-tasks.md §8 (Tap 流程) + §11.3.
//
// **No LIFF required.** Tap-to-scan is fully public:
//   1. Parse SDM params (uid/sdmcounter/sdmmac, hex on the wire).
//   2. POST /v1/patrols/scan/match — public, CMAC-verified. Backend
//      writes patrol_event + issues a scan_session_token (KV-backed,
//      5 min TTL). Response carries location_label, site_label,
//      tapped_at, identity_resolved, officer_display_name, etc.
//   3. Two outcomes:
//        a) `assignment_count == 0` → render attendance receipt with
//           location / site / identity / time + an optional identity
//           verify CTA. Officer can complete passkey or face liveness
//           to back-fill patrol_events.officer_id; if they don't, the
//           tap is recorded as 訪客 (visitor).
//        b) `assignment_count > 0` → redirect to /scan/wizard?token=...
//           where the wizard runs identity verify (device / face) +
//           Step 2 / Step 3.

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, Loader2, ShieldCheck,
} from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { apiFetch } from "../../lib/api-client.js";

const FACEAPI_LIB_URL = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACEAPI_MODELS_URL = "https://justadudewhohacks.github.io/face-api.js/models";

type AuthOptionsJSON = Parameters<typeof startAuthentication>[0]["optionsJSON"];

type Phase = "init" | "matching" | "redirecting" | "no_tasks" | "error";
type IdentityState = "visitor" | "verifying_device" | "verifying_face" | "identified";

interface MatchResponse {
  ok: boolean;
  scan_session_token: string;
  expires_at: number;
  patrol_event_id: string;
  location_id: string;
  location_label: string | null;
  site_id: string | null;
  site_label: string | null;
  tenant_id: string;
  tapped_at: number;
  assignment_count: number;
  identity_resolved: boolean;
  officer_display_name: string | null;
  correlation_id?: string;
  error?: string;
}

interface FaceApi {
  nets: {
    tinyFaceDetector: { loadFromUri(u: string): Promise<void> };
    faceLandmark68Net: { loadFromUri(u: string): Promise<void> };
    faceRecognitionNet: { loadFromUri(u: string): Promise<void> };
  };
  TinyFaceDetectorOptions: new (opts?: { inputSize?: number; scoreThreshold?: number }) => unknown;
  detectSingleFace(input: HTMLVideoElement | HTMLCanvasElement, opts?: unknown): {
    withFaceLandmarks(): { withFaceDescriptor(): Promise<{
      detection: { box: { x: number; y: number; width: number; height: number } };
      landmarks: { positions: { x: number; y: number }[] };
      descriptor: Float32Array;
    } | undefined> };
  };
}
declare global { interface Window { faceapi?: FaceApi } }

function pickParam(p: URLSearchParams, ...names: string[]): string | null {
  for (const n of names) {
    const v = p.get(n);
    if (v != null && v !== "") return v;
  }
  return null;
}

export default function ScanPage(): React.ReactElement {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>("init");
  const [error, setError] = useState<string | null>(null);
  const [match, setMatch] = useState<MatchResponse | null>(null);
  const [identity, setIdentity] = useState<IdentityState>("visitor");
  const [identityName, setIdentityName] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  useEffect(() => {
    const uid = pickParam(params, "uid", "UID", "picc")?.toUpperCase() ?? null;
    const ctrRaw = pickParam(params, "sdmcounter", "ctr") ?? "";
    const ctr = parseInt(ctrRaw, 16);                            // hex per NXP AN12196
    const mac = pickParam(params, "sdmmac", "mac", "cmac")?.toUpperCase() ?? null;
    const nonce = pickParam(params, "nonce");

    if (!uid || !mac || !Number.isFinite(ctr)) {
      setError("URL 缺少必要的加密巡邏標籤參數（uid / sdmcounter / sdmmac）。");
      setPhase("error");
      return;
    }

    setPhase("matching");
    void (async () => {
      try {
        const resp = await apiFetch("/v1/patrols/scan/match", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ uid, sdmcounter: ctr, sdmmac: mac, nonce }),
        });
        const json = (await resp.json().catch(() => ({}))) as Partial<MatchResponse> & { error?: string };
        if (!resp.ok || !json.scan_session_token) {
          setError(json.error ?? `HTTP ${resp.status}`);
          setPhase("error");
          return;
        }
        const m = json as MatchResponse;
        setMatch(m);
        if (m.identity_resolved && m.officer_display_name) {
          setIdentity("identified");
          setIdentityName(m.officer_display_name);
        }

        if ((m.assignment_count ?? 0) === 0) {
          setPhase("no_tasks");
          return;
        }

        setPhase("redirecting");
        const next = new URL("/scan/wizard", window.location.origin);
        next.searchParams.set("token", m.scan_session_token);
        window.location.replace(next.toString());
      } catch (e) {
        setError((e as Error).message ?? "network_error");
        setPhase("error");
      }
    })();
    // run once on mount with the URL params we read above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Identity verify (device → face fallback) ────────────────────────────
  const runFaceVerify = useCallback(async (token: string): Promise<void> => {
    setIdentity("verifying_face");
    setVerifyError(null);
    try {
      const cr = await apiFetch("/v1/patrols/scan/verify/face/challenge", {
        headers: { "x-scan-session": token },
      });
      const challenge = (await cr.json().catch(() => ({}))) as
        { challenge_id?: string; action?: string; error?: string };
      if (!cr.ok || !challenge.challenge_id) {
        setVerifyError(friendlyVerifyError(challenge.error ?? `HTTP ${cr.status}`));
        setIdentity("visitor");
        return;
      }
      if (!window.faceapi) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement("script");
          s.src = FACEAPI_LIB_URL;
          s.async = true;
          s.onload = () => resolve();
          s.onerror = () => reject(new Error("face-api.js 載入失敗"));
          document.head.appendChild(s);
        });
      }
      const fa = window.faceapi!;
      await Promise.all([
        fa.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODELS_URL),
        fa.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODELS_URL),
        fa.nets.faceRecognitionNet.loadFromUri(FACEAPI_MODELS_URL),
      ]);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((r) => setTimeout(r, 800));
      const det = await fa
        .detectSingleFace(video, new fa.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
        .withFaceLandmarks().withFaceDescriptor();
      if (!det) {
        stream.getTracks().forEach((t) => t.stop());
        setVerifyError("未偵測到人臉，請正視鏡頭重試。");
        setIdentity("visitor");
        return;
      }
      const size = 320;
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      ctx.translate(size, 0); ctx.scale(-1, 1);
      ctx.drawImage(
        video,
        det.detection.box.x, det.detection.box.y,
        det.detection.box.width, det.detection.box.height,
        0, 0, size, size,
      );
      const photoB64 = canvas.toDataURL("image/jpeg", 0.85);
      stream.getTracks().forEach((t) => t.stop());

      const fr = await apiFetch("/v1/patrols/scan/verify/face/finish", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        body: JSON.stringify({
          challenge_id: challenge.challenge_id,
          action: challenge.action,
          completed_at: Date.now(),
          descriptor: Array.from(det.descriptor),
          photo_b64: photoB64.replace(/^data:image\/[a-z]+;base64,/, ""),
        }),
      });
      const fb = (await fr.json().catch(() => ({}))) as
        { ok?: boolean; officer_display_name?: string | null; error?: string };
      if (!fr.ok || !fb.ok) {
        setVerifyError(friendlyVerifyError(fb.error ?? `HTTP ${fr.status}`));
        setIdentity("visitor");
        return;
      }
      setIdentity("identified");
      setIdentityName(fb.officer_display_name ?? null);
    } catch (e) {
      setVerifyError((e as Error).message ?? "verify_failed");
      setIdentity("visitor");
    }
  }, []);

  const runIdentityVerify = useCallback(async (): Promise<void> => {
    if (!match) return;
    const token = match.scan_session_token;
    setVerifyError(null);

    // No WebAuthn API on this browser → straight to face.
    if (typeof window === "undefined" || !window.PublicKeyCredential) {
      await runFaceVerify(token);
      return;
    }

    setIdentity("verifying_device");
    try {
      const startResp = await apiFetch("/v1/patrols/scan/verify/device/start", {
        method: "POST",
        headers: { "x-scan-session": token },
      });
      const startBody = (await startResp.json().catch(() => ({}))) as
        { options?: AuthOptionsJSON; error?: string };
      if (!startResp.ok || !startBody.options) {
        // No registered passkey on this tenant or backend bootstrap issue
        // → fall through to face.
        await runFaceVerify(token);
        return;
      }
      let assertion;
      try {
        assertion = await startAuthentication({ optionsJSON: startBody.options });
      } catch (err) {
        const name = (err as Error).name ?? String(err);
        if (name === "NotAllowedError") {
          // User cancelled the platform biometric prompt — respect that,
          // don't auto-bounce them into face capture.
          setVerifyError("已取消裝置驗證");
          setIdentity("visitor");
          return;
        }
        // Other errors (no credential on this device, transport issue) →
        // try face fallback.
        await runFaceVerify(token);
        return;
      }
      const finishResp = await apiFetch("/v1/patrols/scan/verify/device/finish", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        body: JSON.stringify({ assertionResponse: assertion }),
      });
      const finishBody = (await finishResp.json().catch(() => ({}))) as
        { ok?: boolean; officer_display_name?: string | null; error?: string };
      if (!finishResp.ok || !finishBody.ok) {
        if (finishBody.error === "credential_not_recognised_in_tenant") {
          // Device's passkey isn't bound to any officer here — face is
          // the only remaining identity option.
          await runFaceVerify(token);
          return;
        }
        setVerifyError(friendlyVerifyError(finishBody.error ?? `HTTP ${finishResp.status}`));
        setIdentity("visitor");
        return;
      }
      setIdentity("identified");
      setIdentityName(finishBody.officer_display_name ?? null);
    } catch (e) {
      setVerifyError((e as Error).message ?? "verify_failed");
      setIdentity("visitor");
    }
  }, [match, runFaceVerify]);

  // ── Render ──────────────────────────────────────────────────────────────
  if (phase === "init" || phase === "matching" || phase === "redirecting") {
    return (
      <Frame>
        <Loader2 className="h-12 w-12 animate-spin text-accent mb-4" />
        <p className="text-sm text-text-secondary">
          {phase === "matching" ? "驗證加密巡邏標籤中…"
            : phase === "redirecting" ? "進入任務精靈…"
            : "讀取中…"}
        </p>
      </Frame>
    );
  }

  if (phase === "no_tasks" && match) {
    return (
      <Frame>
        <CheckCircle2 className="h-12 w-12 text-success mb-4" />
        <p className="text-base font-medium mb-5">已記錄到站</p>

        <ReceiptCard
          locationLabel={match.location_label}
          siteLabel={match.site_label}
          identity={identity}
          identityName={identityName}
          tappedAt={match.tapped_at}
        />

        <p className="text-xs text-text-muted text-center mt-3">
          目前此地點沒有需要執行的任務。
        </p>

        {identity !== "identified" && (
          <div className="mt-5 w-full">
            <button
              onClick={() => void runIdentityVerify()}
              disabled={identity === "verifying_device" || identity === "verifying_face"}
              className="w-full h-12 rounded-xl bg-accent text-text-inverse text-sm font-semibold disabled:bg-bg-raised disabled:text-text-muted inline-flex items-center justify-center gap-2"
            >
              {identity === "verifying_device" || identity === "verifying_face"
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ShieldCheck className="h-4 w-4" />}
              {identity === "verifying_device" ? "驗證裝置中…"
                : identity === "verifying_face" ? "辨識人臉中…"
                : "我是巡邏員 — 驗證身分"}
            </button>
            <p className="text-2xs text-text-muted text-center mt-2">
              未驗證時以訪客身分留存。驗證後會關聯到您的帳號並計入班次。
            </p>
            {verifyError && (
              <p className="text-2xs text-danger text-center mt-2">{verifyError}</p>
            )}
          </div>
        )}

        <p className="text-2xs text-text-muted/60 text-center mt-6 font-mono">
          ref · {refCode(match.patrol_event_id)}
        </p>
      </Frame>
    );
  }

  return (
    <Frame>
      <AlertTriangle className="h-12 w-12 text-danger mb-4" />
      <p className="text-base font-medium mb-1">驗證失敗</p>
      <p className="text-sm text-text-secondary text-center max-w-sm mb-2">{error ?? "未知錯誤"}</p>
    </Frame>
  );
}

function ReceiptCard({
  locationLabel, siteLabel, identity, identityName, tappedAt,
}: {
  locationLabel: string | null;
  siteLabel: string | null;
  identity: IdentityState;
  identityName: string | null;
  tappedAt: number;
}): React.ReactElement {
  return (
    <div className="w-full rounded-xl border border-border-subtle bg-bg-raised p-4 space-y-2.5 text-sm">
      <Row label="地點" value={locationLabel ?? "—"} />
      <Row label="案場" value={siteLabel ?? "—"} />
      <Row
        label="身分"
        value={
          identity === "identified"
            ? <span className="text-text-primary">{identityName ?? "已驗證"} <span className="text-success">✓</span></span>
            : identity === "verifying_device" || identity === "verifying_face"
            ? <span className="text-text-muted inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />驗證中…</span>
            : <span className="text-text-muted">訪客（未驗證身分）</span>
        }
      />
      <Row label="時間" value={formatTime(tappedAt)} mono />
    </div>
  );
}

function Row({ label, value, mono }: {
  label: string; value: React.ReactNode; mono?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-text-muted shrink-0">{label}</span>
      <span className={`text-right truncate ${mono ? "font-mono tabular-nums" : ""}`}>{value}</span>
    </div>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function refCode(eventId: string): string {
  const tail = eventId.slice(-8).toUpperCase();
  return tail.length >= 8 ? tail : eventId.toUpperCase();
}

function friendlyVerifyError(code: string): string {
  switch (code) {
    case "credential_not_recognised_in_tenant":
      return "此裝置尚未註冊到任何巡邏員，已切換至人臉辨識。";
    case "no_face_enrolled":
      return "尚未由管理員建立人臉註冊資料。";
    case "challenge_expired":
      return "驗證已逾時，請再試一次。";
    case "verification_failed":
      return "驗證失敗，請再試一次。";
    case "scan_session_invalid_or_expired":
      return "感應紀錄已逾時，請重新感應加密巡邏標籤。";
    default:
      return code;
  }
}

function Frame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="min-h-screen bg-bg-base text-text-base flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md flex flex-col items-center">
        <header className="flex items-center gap-2 mb-6 mt-2">
          <img
            src="/sentry-icon.svg"
            alt="SENTRY"
            className="h-8 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <span className="text-sm font-semibold tracking-wide">NEXUS</span>
        </header>
        {children}
      </div>
    </div>
  );
}
